import { TierLabel, TierVersion, } from "@memento/types/subscription";
import { buildNoneSubscription, getHigherTier, getOrderedTierLabels, tierLabelToHuman, } from "../subscription.js";
import { FeatureAccessTierNoneV1, FeatureAccessTierPlusV1, FeatureAccessTierPremiumV1, FeatureAccessTierPlatinumV1, FeatureAccessTierPreferredV1, FeatureAccessTierExtraV1, FeatureAccessTierAdvancedV1, FeatureAccessTierBusinessV1, } from "./tier-v1.js";
import { Feature, FeatureAccessTier, guardFeature } from "./feature.js";
import { isCardLegacyUnlocked } from "../card.js";
export { Feature, FeatureAccessTier, guardFeature };
export const ACTIVE_FEATURE_TIER_VERSION = TierVersion.V1;
// V1 FeatureAccessTier implementations
const ftNoneV1 = new FeatureAccessTierNoneV1();
const ftExtraV1 = new FeatureAccessTierExtraV1();
const ftAdvancedV1 = new FeatureAccessTierAdvancedV1();
const ftPreferredV1 = new FeatureAccessTierPreferredV1();
const ftPlusV1 = new FeatureAccessTierPlusV1();
const ftPremiumV1 = new FeatureAccessTierPremiumV1();
const ftPlatinumV1 = new FeatureAccessTierPlatinumV1();
const ftBusinessV1 = new FeatureAccessTierBusinessV1();
/** Generate a message that requires the lowest tier supporting a feature. */
export function getLowestTierSupportingMessage(feature, getMessage) {
    const tier = getLowestTierSupporting(feature);
    return getMessage({
        tierLabelHuman: tierLabelToHuman(tier.label),
        tier,
    });
}
export function getLowestTierSupporting(feature) {
    const tierLabels = getOrderedTierLabels();
    for (const tierLabel of tierLabels) {
        const tier = {
            label: tierLabel,
            version: ACTIVE_FEATURE_TIER_VERSION,
        };
        const featureAccessTier = getFeatureAccessTier(tier);
        if (featureAccessTier.canUseFeature(feature)) {
            return tier;
        }
    }
    // This shouldn't happen, but did not find any tier supporting the feature,
    // so just assume it requires the highest tier.
    return {
        label: TierLabel.PLATINUM,
        version: ACTIVE_FEATURE_TIER_VERSION,
    };
}
export function tierSupportsFeature(tierLabel, feature) {
    const tier = {
        label: tierLabel,
        version: ACTIVE_FEATURE_TIER_VERSION,
    };
    const featureAccessTier = getFeatureAccessTier(tier);
    return featureAccessTier.canUseFeature(feature);
}
function tierToKey(tier) {
    return `${tier.label}-${tier.version}`;
}
function getFeatureAccessTier(tier) {
    // Get the associated FeatureTier
    return featureAccessTierLookup[tierToKey(tier)];
}
/** Features available without a subscription if the
 * groupVideosPaywallMovedToRender feature flag is enabled. If we remove this
 * feature flag and keep the feature permanently, move these features to the
 * `noneFeatures` array in `packages/library/src/feature-access/tier-v1.ts` and
 * they'll work for all tier levels (or just remove those features altogether).
 */
const groupVideosPaywallMovedToRenderFeatures = [
    Feature.MontageCreate,
    Feature.ContributorInvite,
    Feature.ViewRecordLink,
];
function getIsGroupVideosPaywallMovedToRenderFeature(feature) {
    return groupVideosPaywallMovedToRenderFeatures.includes(feature);
}
export function canUseFeature(feature, userSubscription, { card = null, otherSubscriptions = null, featureFlags }) {
    if (featureFlags.groupVideosPaywallMovedToRender &&
        getIsGroupVideosPaywallMovedToRenderFeature(feature)) {
        return true;
    }
    // Get the tier that applies
    const tier = getTier(userSubscription, card, otherSubscriptions);
    // Get the associated FeatureTier
    const featureAccessTier = getFeatureAccessTier(tier);
    // Query the feature access tier for this particular feature
    return featureAccessTier.canUseFeature(feature);
}
/**
 * A mapping of Tier to FeatureAccessTiers. Currently contains only
 * TierVersion.V1, but expandable to more.
 */
const featureAccessTierLookup = {
    "NONE-1": ftNoneV1,
    "EXTRA-1": ftExtraV1,
    "ADVANCED-1": ftAdvancedV1,
    "PREFERRED-1": ftPreferredV1,
    "PLUS-1": ftPlusV1,
    "PREMIUM-1": ftPremiumV1,
    "PLATINUM-1": ftPlatinumV1,
    "BUSINESS-1": ftBusinessV1,
};
const baseSubscription = buildNoneSubscription("");
const highestFeatureAccessTier = ftPlatinumV1;
/**
 * Gets the appropriate Tier to use, given a collection of resources.
 * @param userSubscription the subscription of the currently logged-in user
 * @param card the associated card object
 * @returns the best available Tier
 */
function getTier(userSubscription, card = null, 
/** If these aren't on the card itself, then they can be passed in here. */
otherSubscriptions = null) {
    const tiers = getResourceTiers(userSubscription, card, otherSubscriptions);
    // Compare the four tiers and return the highest one
    return getHigherTier(getHigherTier(tiers.ownerTier, tiers.cardTier), getHigherTier(tiers.userTier, tiers.collaboratorTier));
}
function getOtherSubscriptions(card, otherSubscriptions) {
    if (otherSubscriptions) {
        return otherSubscriptions;
    }
    else if (card) {
        return {
            owner: card.subscriptions?.owner ?? null,
            collaborator: card.subscriptions?.collaborator ?? null,
        };
    }
    else {
        return {
            owner: null,
            collaborator: null,
        };
    }
}
/**
 * Gets the appropriate Tiers for a collection of resources.
 * @param userSubscription the subscription of the currently logged-in user
 * @param card the associated card object
 * @returns all Tiers associated with these resources
 */
export function getResourceTiers(userSubscription, card = null, 
/** If these aren't on the card itself, then they can be passed in here. */
otherSubscriptions = null) {
    otherSubscriptions = getOtherSubscriptions(card, otherSubscriptions);
    // Determine the subscription to enforce for the organizer and collaborator
    const userSub = getActiveSubscriptionOrBaseSubscription(userSubscription);
    const ownerSub = getActiveSubscriptionOrBaseSubscription(otherSubscriptions.owner);
    const collaboratorSub = getActiveSubscriptionOrBaseSubscription(otherSubscriptions.collaborator);
    const cardTier = isCardLegacyUnlocked(card)
        ? highestFeatureAccessTier.tier
        : baseSubscription.tier;
    return {
        userTier: userSub.tier,
        ownerTier: ownerSub.tier,
        collaboratorTier: collaboratorSub.tier,
        cardTier: cardTier,
    };
}
function getActiveSubscriptionOrBaseSubscription(subscription) {
    if (!subscription || !subscription.isActive) {
        // If there is no subscription or the supplied subscription is not active,
        // return the base subscription
        return baseSubscription;
    }
    return subscription;
}
export function getHighestFeatureAccessTier() {
    return highestFeatureAccessTier;
}
